/**
 * The learner profile: first launch, and the pre-game baseline captured at setup (TASKS T46b).
 *
 * Two facts live here, and both exist for one reason — the week-6 verdict card (SPEC §8.7) has to
 * be able to say *the design was wrong*. It cannot say that against nothing. It needs a `week 1`
 * origin and it needs the numbers as they stood before the game touched them, and neither can be
 * reconstructed after the fact: by week 6 the pre-game state is unrecoverable, and asking then
 * would produce a number contaminated by six weeks of using the thing being judged.
 *
 * The distinction this module is most careful about is **null versus zero**. A blank field means
 * "not known" and stays null forever; an entered `0` is a measurement and is stored as `0`. The
 * tracker draws the same line at DoD-9, and it matters more here: a baseline that silently defaulted
 * to zero would make every week-6 comparison flattering, which is precisely the failure this card
 * exists to prevent.
 *
 * Pure apart from the two store calls. No clock — `now` is passed in.
 */

import { KEY } from './schema';
import type { Store } from './store';

/** The pre-game numbers, as the learner reported them at setup. */
export interface Baseline {
  /**
   * Retention before this game, percent, if he happened to know it. Null means not known — never
   * 0, which would be a claim that he remembered nothing.
   */
  retentionPct: number | null;
  /** Spanish minutes on a typical day before this game. Null means not answered; 0 is an answer. */
  dailyMinutes: number | null;
  /**
   * When the setup card was answered, or last edited from the tracker.
   *
   * Null is the single source of truth for "never asked". It is one field rather than a separate
   * `captured` boolean so there is nothing for two flags to disagree about.
   */
  recordedAt: number | null;
}

export interface LearnerProfile {
  /**
   * Stamped on the first boot, never rewritten.
   *
   * Deliberately *not* stamped when the setup card is answered: a learner who opens the app and
   * force-quits before finishing setup has still launched it, and week 1 has still started. Dating
   * week 1 from the answer would let the novelty panel's x-axis be moved by a later decision.
   */
  firstLaunchAt: number;
  baseline: Baseline;
  /**
   * When the four-week review was last read (T72). Null until the first one is.
   *
   * The next review is 28 days after this rather than after a calendar boundary, so a learner who
   * opens it late gets the next one 28 days after he *read* it. Nothing nags in between.
   */
  lastReview28At?: number;
  /**
   * New items a day, if the learner has ever changed it from the default (T72).
   *
   * Absent until a four-week review's recommendation is explicitly accepted. It is stored on the
   * profile rather than derived, because it is a decision he made and the app must not quietly
   * revisit it.
   */
  newCapPerDay?: number;
  /**
   * When the one-off conversion demonstration was shown (`src/modes/demostracion.ts`).
   *
   * Absent until it has been. Recorded rather than inferred for the reason `introducedAt` is
   * (D-213): an inferred "already seen" is indistinguishable from a bug, and it is wrong for every
   * store that predates the feature.
   */
  demoSeenAt?: number;
}

export const PROFILE_KEY = KEY.meta('profile');

export function emptyBaseline(): Baseline {
  return { retentionPct: null, dailyMinutes: null, recordedAt: null };
}

/**
 * Read the profile, creating and persisting it if this is the first launch.
 *
 * Written immediately rather than on first change, because `firstLaunchAt` is only correct if it is
 * captured at the launch it names.
 */
export async function loadProfile(store: Store, now: number): Promise<LearnerProfile> {
  const found = await store.get<LearnerProfile>(PROFILE_KEY);
  if (found) return found;
  const created: LearnerProfile = { firstLaunchAt: now, baseline: emptyBaseline() };
  await store.set(PROFILE_KEY, created);
  return created;
}

/** Has the setup card been answered? False exactly once in the app's lifetime. */
export function needsSetup(profile: LearnerProfile): boolean {
  return profile.baseline.recordedAt === null;
}

/**
 * Record the baseline. Returns the updated profile.
 *
 * `firstLaunchAt` is carried across untouched, so neither answering the card late nor editing the
 * baseline from the tracker can move week 1.
 */
/** Record that the four-week review has been read, and start the next 28 days (T72). */
export async function markReviewRead(
  store: Store,
  profile: LearnerProfile,
  at: number,
): Promise<LearnerProfile> {
  const next: LearnerProfile = { ...profile, lastReview28At: at };
  await store.set(PROFILE_KEY, next);
  return next;
}

/**
 * Record that the conversion demonstration has been shown (`src/modes/demostracion.ts`).
 *
 * Written **before** the transition into the run, so a tab closed on the demonstration card has
 * still seen it. Showing it twice would be worse than not showing it at all: the second time it is
 * no longer a word he was never taught, and the claim the card makes stops being true.
 */
export async function markDemoSeen(
  store: Store,
  profile: LearnerProfile,
  at: number,
): Promise<LearnerProfile> {
  const next: LearnerProfile = { ...profile, demoSeenAt: at };
  await store.set(PROFILE_KEY, next);
  return next;
}

/**
 * Accept a four-week review's intake recommendation (T72, DoD-10).
 *
 * Only ever reached by an explicit tap. Nothing in the app calls this on its own — an app that
 * quietly retuned itself every month would make its own behaviour unaccountable.
 */
export async function saveIntakeCap(
  store: Store,
  profile: LearnerProfile,
  newCapPerDay: number,
  at: number,
): Promise<LearnerProfile> {
  const next: LearnerProfile = { ...profile, newCapPerDay, lastReview28At: at };
  await store.set(PROFILE_KEY, next);
  return next;
}

export async function saveBaseline(
  store: Store,
  profile: LearnerProfile,
  baseline: Pick<Baseline, 'retentionPct' | 'dailyMinutes'>,
  now: number,
): Promise<LearnerProfile> {
  /*
   * Spread, not rebuilt.
   *
   * This used to construct the profile from two fields, which silently dropped every other one.
   * It never bit at first launch — nothing else exists yet — but the baseline is editable later
   * from El Tablero (D-102), and editing it there discarded an accepted intake cap, the four-week
   * review marker, and the record that the conversion demonstration had been seen. Data loss, in a
   * function whose whole job is to persist.
   */
  const updated: LearnerProfile = {
    ...profile,
    baseline: {
      retentionPct: baseline.retentionPct,
      dailyMinutes: baseline.dailyMinutes,
      recordedAt: now,
    },
  };
  await store.set(PROFILE_KEY, updated);
  return updated;
}

// ---------------------------------------------------------------------------------------------
// Field parsing
// ---------------------------------------------------------------------------------------------

export type BaselineField = 'retentionPct' | 'dailyMinutes';

interface FieldRange {
  min: number;
  max: number;
  /** Used in the message the card shows. Plain words, no field names. */
  label: string;
}

/**
 * The accepted range per field.
 *
 * `dailyMinutes` tops out at a day. Not defensive programming for its own sake: the fat-finger
 * failure here is a stray digit, and a baseline of 3000 minutes a day would make the week-6 verdict
 * read as a catastrophic collapse forever, with no way to tell from the card that the input was the
 * problem.
 */
const RANGE: Readonly<Record<BaselineField, FieldRange>> = {
  retentionPct: { min: 0, max: 100, label: 'Retention is a percentage, 0 to 100' },
  dailyMinutes: { min: 0, max: 1440, label: 'Minutes must be between 0 and 1440' },
};

export type FieldParse = { ok: true; value: number | null } | { ok: false; reason: string };

/**
 * Parse one baseline field from what was typed.
 *
 * Blank is valid and means null. Anything unparseable or out of range is **refused** rather than
 * coerced: silently dropping a typo would store "not known" while the learner believes he answered,
 * and silently clamping would store a number he never gave.
 */
export function parseBaselineField(raw: string, field: BaselineField): FieldParse {
  const trimmed = raw.trim();
  if (trimmed === '') return { ok: true, value: null };

  const range = RANGE[field];
  const value = Number(trimmed);
  if (!Number.isFinite(value)) return { ok: false, reason: `${range.label}.` };
  if (value < range.min || value > range.max) return { ok: false, reason: `${range.label}.` };
  // Whole numbers only. A baseline of 87.3% retention is false precision on a remembered figure.
  return { ok: true, value: Math.round(value) };
}

export function baselineFieldRange(field: BaselineField): { min: number; max: number } {
  return { min: RANGE[field].min, max: RANGE[field].max };
}

// ---------------------------------------------------------------------------------------------
// Week number
// ---------------------------------------------------------------------------------------------

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Weeks since first launch, 1-based. The novelty panel's x-axis (SPEC §8.7).
 *
 * Elapsed time, not calendar weeks: a Saturday install must not make week 1 two days long, or the
 * weeks-1-to-12 comparison starts with a short bucket that reads as a slump.
 */
export function weekNumber(firstLaunchAt: number, now: number): number {
  if (now <= firstLaunchAt) return 1;
  return Math.floor((now - firstLaunchAt) / WEEK_MS) + 1;
}
